import { useEffect, useState } from "react";
import { api, getApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, KeyRound, Copy } from "lucide-react";
import { toast } from "sonner";

/**
 * 2FA enrollment / disable flow for the currently authenticated user
 * (typically the admin). Uses a free public QR endpoint to render the
 * provisioning URI — no extra dependencies.
 */
export default function AdminSecurity() {
  const [status, setStatus] = useState({ totp_enabled: false, enabled_at: null });
  const [setup, setSetup] = useState(null);  // { secret, otpauth_uri }
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [disabling, setDisabling] = useState(false);
  const [disablePw, setDisablePw] = useState("");
  const [disableCode, setDisableCode] = useState("");

  const refresh = async () => {
    try {
      const r = await api.get("/auth/2fa/status");
      setStatus(r.data);
    } catch (e) { toast.error(getApiError(e)); }
  };

  useEffect(() => { refresh(); }, []);

  const startSetup = async () => {
    setBusy(true);
    try {
      const r = await api.post("/auth/2fa/setup");
      setSetup(r.data);
    } catch (e) { toast.error(getApiError(e)); }
    finally { setBusy(false); }
  };

  const verify = async () => {
    if (code.length !== 6) return;
    setBusy(true);
    try {
      await api.post("/auth/2fa/verify", { code });
      toast.success("Two-factor authentication enabled");
      setSetup(null);
      setCode("");
      refresh();
    } catch (e) { toast.error(getApiError(e)); }
    finally { setBusy(false); }
  };

  const disable = async () => {
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { password: disablePw, code: disableCode });
      toast.success("Two-factor authentication disabled");
      setDisabling(false);
      setDisablePw("");
      setDisableCode("");
      refresh();
    } catch (e) { toast.error(getApiError(e)); }
    finally { setBusy(false); }
  };

  const qrUrl = setup
    ? `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(setup.otpauth_uri)}`
    : null;

  return (
    <Card className="mt-4 border-black/5" data-testid="admin-security-card">
      <CardContent className="p-6 space-y-5">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h3 className="font-serif text-2xl flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-[color:var(--fire-red)]" />
              Two-factor authentication
            </h3>
            <p className="text-sm text-[color:var(--text-secondary)] mt-1 max-w-xl">
              Protect your trustee account with a 6-digit code from an authenticator app
              (Google Authenticator, 1Password, Authy, etc.). Required for every sign-in once enabled.
            </p>
          </div>
          {status.totp_enabled ? (
            <Badge className="bg-[color:var(--bright-green)] text-white" data-testid="admin-2fa-status-on">
              <ShieldCheck className="h-3 w-3 mr-1" />Enabled
            </Badge>
          ) : (
            <Badge variant="outline" className="border-black/10" data-testid="admin-2fa-status-off">Not enabled</Badge>
          )}
        </div>

        {/* Brute-force notice */}
        <div className="rounded-md border border-black/5 bg-[color:var(--bg-secondary)] p-3 text-xs text-[color:var(--text-secondary)] flex items-start gap-2" data-testid="admin-bruteforce-notice">
          <KeyRound className="h-3.5 w-3.5 mt-0.5 text-[color:var(--orange)] flex-shrink-0" />
          <span>
            <strong className="text-[color:var(--text-primary)]">Brute-force protection active:</strong>{" "}
            5 failed sign-ins on the same email/IP triggers a 15-minute lockout. Affects all accounts including this one.
          </span>
        </div>

        {/* Setup flow */}
        {!status.totp_enabled && !setup && (
          <Button onClick={startSetup} disabled={busy} className="bg-[color:var(--fire-red)] hover:bg-[color:var(--fire-red-hover)] text-white" data-testid="admin-2fa-start-btn">
            {busy ? "Generating…" : "Enable two-factor authentication"}
          </Button>
        )}

        {!status.totp_enabled && setup && (
          <div className="space-y-4" data-testid="admin-2fa-setup-panel">
            <div className="grid sm:grid-cols-[220px,1fr] gap-4 items-start">
              <img src={qrUrl} alt="Scan with your authenticator app" className="rounded-md border border-black/5" data-testid="admin-2fa-qr" />
              <div className="space-y-3 text-sm">
                <p>
                  <strong>1.</strong> Scan this QR code with your authenticator app.
                </p>
                <p className="text-[color:var(--text-secondary)]">
                  Or paste the secret manually:
                </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-[color:var(--bg-secondary)] px-2 py-1.5 rounded font-mono text-xs break-all" data-testid="admin-2fa-secret">
                    {setup.secret}
                  </code>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-black/10"
                    onClick={() => { navigator.clipboard.writeText(setup.secret); toast.success("Secret copied"); }}
                    data-testid="admin-2fa-copy-btn"
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                </div>
                <p>
                  <strong>2.</strong> Enter the 6-digit code from your app to confirm.
                </p>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    placeholder="123456"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
                    className="font-mono tracking-[0.4em] text-center"
                    data-testid="admin-2fa-code-input"
                  />
                  <Button
                    onClick={verify}
                    disabled={busy || code.length !== 6}
                    className="bg-[color:var(--fire-red)] hover:bg-[color:var(--fire-red-hover)] text-white"
                    data-testid="admin-2fa-verify-btn"
                  >
                    Verify
                  </Button>
                </div>
                <button onClick={() => { setSetup(null); setCode(""); }} className="text-xs text-[color:var(--text-secondary)] hover:text-[color:var(--fire-red)]" data-testid="admin-2fa-cancel-btn">
                  Cancel setup
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Disable flow */}
        {status.totp_enabled && !disabling && (
          <Button variant="outline" className="border-black/10" onClick={() => setDisabling(true)} data-testid="admin-2fa-disable-btn">
            Disable two-factor authentication
          </Button>
        )}

        {status.totp_enabled && disabling && (
          <div className="space-y-3 border border-black/10 rounded-md p-4" data-testid="admin-2fa-disable-panel">
            <p className="text-sm">To disable 2FA, confirm your password and a current 6-digit code.</p>
            <div>
              <Label htmlFor="dpw">Password</Label>
              <Input id="dpw" type="password" value={disablePw} onChange={(e) => setDisablePw(e.target.value)} data-testid="admin-2fa-disable-pw" />
            </div>
            <div>
              <Label htmlFor="dcode">6-digit code</Label>
              <Input id="dcode" type="text" inputMode="numeric" maxLength={6} value={disableCode}
                     onChange={(e) => setDisableCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
                     className="font-mono tracking-[0.4em] text-center"
                     data-testid="admin-2fa-disable-code" />
            </div>
            <div className="flex gap-2">
              <Button onClick={disable} disabled={busy || !disablePw || disableCode.length !== 6} className="bg-[color:var(--fire-red)] hover:bg-[color:var(--fire-red-hover)] text-white" data-testid="admin-2fa-disable-confirm">
                Disable
              </Button>
              <Button variant="outline" className="border-black/10" onClick={() => { setDisabling(false); setDisablePw(""); setDisableCode(""); }}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
